/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import DetailList from './DetailList';
import ExportButton from '../../active-users/ExportButton';
import { q, num, Kpi } from './kit';
import { formatUTCDate } from '../../../utils/utils';
import { formatDurationMs } from '../../../utils/formatDurationMs';

// '1' → Yes, anything else → No — the same rule the detail page applies.
const yesNo = (v) => (v === '1' ? 'Yes' : 'No');
const dash = (v) => (v === null || v === undefined || v === '' ? '-' : v);
const ts = (v) => formatUTCDate(v) || '-';
// HR averages arrive null when the filtered set holds no numeric samples (the TEXT-column
// cast guard), so an em dash — never "NaN bpm" — is the empty reading.
const bpm = (v) => (v == null ? '—' : `${Math.round(Number(v))}`);

// The table mirrors /standing-check-details/:id exactly: same headers, same order,
// same formatting. The derived Rise/Category columns this tab used to show are gone —
// they existed nowhere on the detail page, so a number read here did not match the
// number read there. Anything derived belongs in the KPI row above, not in the grid.
const COLUMNS = [
  { label: 'No', className: 'tnum', render: (r, i) => i + 1 },
  { label: 'Peak HR', className: 'tnum', render: (r) => dash(r.peak_hr) },
  { label: 'Baseline HR', className: 'tnum', render: (r) => dash(r.baseline_hr) },
  { label: 'Time to Peak', className: 'tnum', render: (r) => dash(r.time_to_peak) },
  // Milliseconds shown as "1m 30s" here; the CSV keeps the raw milliseconds (exportLabel).
  { label: 'Selected Sit Time', exportLabel: 'Selected Sit Time (ms)', className: 'tnum', render: (r) => formatDurationMs(r.selected_sit_time) },
  { label: 'Selected Stand Time', exportLabel: 'Selected Stand Time (ms)', className: 'tnum', render: (r) => formatDurationMs(r.selected_stand_time) },
  { label: 'Test Aborted', render: (r) => yesNo(r.test_aborted) },
  { label: 'Platform', render: (r) => dash(r.platform) },
  // The only sortable column, matching the detail page — it is also the field the
  // endpoint whitelists (VALID_DETAIL_SORT_FIELDS), so nothing else could sort anyway.
  { label: 'Timestamp UTC', sortField: 'timestamp_utc', render: (r) => (r.timestamp_utc ? formatUTCDate(r.timestamp_utc) : '-') },
  { label: 'Extra Detail', className: 'au-ellipsis sc-extra', render: (r) => dash(r.extra_detail) },
  { label: 'In Doctor Report', render: (r) => yesNo(r.in_doctor_report) },
  { label: 'Stand Timestamp', render: (r) => ts(r.stand_time_stamp) },
  { label: 'Peak Timestamp', render: (r) => ts(r.peak_time_stamp) },
  { label: 'Sit Start Timestamp', render: (r) => ts(r.sit_start_time_stamp) },
];

const EXPORT_KEYS = {
  'Peak HR': 'peak_hr', 'Baseline HR': 'baseline_hr', 'Time to Peak': 'time_to_peak',
  'Selected Sit Time': 'selected_sit_time', 'Selected Stand Time': 'selected_stand_time',
  'Test Aborted': 'test_aborted', 'Platform': 'platform', 'Timestamp UTC': 'timestamp_utc',
  'Extra Detail': 'extra_detail', 'In Doctor Report': 'in_doctor_report',
  'Stand Timestamp': 'stand_time_stamp', 'Peak Timestamp': 'peak_time_stamp',
  'Sit Start Timestamp': 'sit_start_time_stamp',
};

// One source of truth for the initial ordering: DetailList starts here, and the export
// mirror is seeded from it, so the two cannot drift apart.
const DEFAULT_SORT = { sortField: 'timestamp_utc', sortOrder: 'DESC' };

// Same CSV contract as the detail page (§7): flags spelled Yes/No, timestamp pre-formatted.
const EXPORT_COLUMNS = COLUMNS.filter((c) => c.label !== 'No').map((c) => ({
  key: EXPORT_KEYS[c.label], label: c.exportLabel || c.label,
}));

const StandingChecksTab = ({ userId, from, to, authGet }) => {
  // Sort lives in DetailList; this mirrors it so the export can follow the view.
  const [sort, setSort] = useState(DEFAULT_SORT);

  // The export re-fetches every filtered row from the dedicated endpoint rather than
  // dumping the 20 on screen, and carries the active sort so the file matches the view.
  const fetchExport = async () => {
    const res = await authGet(`/admin/api/standing-check-detail-export?${q(userId, from, to, sort)}`);
    return (res.data.data || []).map((r) => ({
      ...r,
      test_aborted: yesNo(r.test_aborted),
      in_doctor_report: yesNo(r.in_doctor_report),
      timestamp_utc: r.timestamp_utc ? formatUTCDate(r.timestamp_utc) : '',
    }));
  };

  // Only the KPI row needs the summary; the table is a plain paginated DetailList over
  // the same endpoint the detail page uses, so both screens page 20 at a time.
  const { status, data, reload } = useTabData(
    async () => {
      const res = await authGet(`/admin/api/standing-check-detail-summary?${q(userId, from, to)}`);
      return res.data.data || {};
    },
    [userId, from, to],
  );

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const summary = data;
  if (!(Number(summary.total_records) > 0)) {
    return <TabEmpty title="No standing checks" message="This user has no standing-check tests in the selected window." icon="◇" />;
  }
  return (
    <div data-testid="ud-standing">
      <div className="ud-kpis">
        <Kpi label="Tests completed" value={num(summary.total_records, 0)} sub={`${num(summary.aborted_count, 0)} cut short`} />
        <Kpi label="In doctor report" value={num(summary.in_doctor_report_count, 0)} />
        <Kpi label="Avg Peak HR" value={bpm(summary.avg_peak_hr)} unit={summary.avg_peak_hr == null ? '' : 'bpm'} />
        <Kpi label="Avg Baseline HR" value={bpm(summary.avg_baseline_hr)} unit={summary.avg_baseline_hr == null ? '' : 'bpm'} />
      </div>

      <div className="au-panel__head">
        <h4 className="au-panel__title">Standing check records</h4>
        <span className="au-spacer" />
        <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="standing-check-details.csv" />
      </div>
      <DetailList
        userId={userId}
        from={from}
        to={to}
        authGet={authGet}
        endpoint="standing-check-details"
        testId="ud-sc-table"
        columns={COLUMNS}
        defaultSortField={DEFAULT_SORT.sortField}
        defaultSortOrder={DEFAULT_SORT.sortOrder}
        onSortChange={setSort}
        empty={{ title: 'No standing checks', message: 'This user has no standing-check tests in the selected window.', icon: '◇' }}
      />
    </div>
  );
};

export default StandingChecksTab;
